import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth'
import { userApi } from '@/api/user'
import { normalizeUserInfo, resolveUserScope } from '@/utils/userScope'

const LOGIN_SESSION_DURATION_MS = 30 * 24 * 60 * 60 * 1000
const TOKEN_EXPIRES_AT_KEY = 'tokenExpiresAt'

function isJwtExpired(rawToken) {
  try {
    const payloadPart = rawToken.split('.')[1]
    if (!payloadPart) return true

    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    const payload = JSON.parse(atob(padded))
    const expiresAt = Number(payload.exp)
    return !Number.isFinite(expiresAt) || expiresAt <= Date.now() / 1000
  } catch {
    return true
  }
}

function getStoredSession() {
  if (typeof localStorage === 'undefined') return { token: '', expiresAt: 0 }
  const storedToken = localStorage.getItem('token') || ''
  if (!storedToken || isJwtExpired(storedToken)) {
    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
    localStorage.removeItem(TOKEN_EXPIRES_AT_KEY)
    return { token: '', expiresAt: 0 }
  }

  const storedExpiresAt = Number(localStorage.getItem(TOKEN_EXPIRES_AT_KEY)) || 0
  const expiresAt = storedExpiresAt || Date.now() + LOGIN_SESSION_DURATION_MS

  if (expiresAt <= Date.now()) {
    localStorage.removeItem('token')
    localStorage.removeItem(TOKEN_EXPIRES_AT_KEY)
    return { token: '', expiresAt: 0 }
  }

  localStorage.setItem(TOKEN_EXPIRES_AT_KEY, String(expiresAt))
  return { token: storedToken, expiresAt }
}

export const useUserStore = defineStore('user', () => {
  const storedSession = getStoredSession()
  const userInfo = ref(null)
  const token = ref(storedSession.token)
  const tokenExpiresAt = ref(storedSession.expiresAt)
  const sessionClock = ref(Date.now())
  let sessionClockTimer = null

  function startSessionClock() {
    if (typeof window === 'undefined' || sessionClockTimer || !token.value) return
    sessionClockTimer = window.setInterval(() => {
      sessionClock.value = Date.now()
      if (tokenExpiresAt.value <= sessionClock.value) {
        logout()
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.replace('/login')
        }
      }
    }, 60 * 1000)
  }

  function stopSessionClock() {
    if (typeof window === 'undefined' || !sessionClockTimer) return
    window.clearInterval(sessionClockTimer)
    sessionClockTimer = null
  }

  startSessionClock()
  // 支付平台返回的企业总额度缓存：每次调用 payOrder（marketing-service 的
  // /orders/{order_no}/pay）后，响应中的 quota 字段会写回这里，供顶部余额卡、
  // 使用统计页等展示。null 表示尚未获取过；0 表示已使用完。
  const remainingTotalQuota = ref(null)
  const usedTotalQuota = ref(null)
  const totalQuota = ref(null)
  const lastPaymentCallId = ref(null)

  const isLoggedIn = computed(() => !!token.value)
  const userName = computed(() => userInfo.value?.username || '')
  const userEmail = computed(() => userInfo.value?.email || '')
  const userScope = computed(() => resolveUserScope(userInfo.value))
  const tenantType = computed(() => userScope.value.tenantType)
  const planType = computed(() => userScope.value.planType)
  // 【2026-10 部门智能体授权】由 /users/me 返回的 department_id，
  // request.js 拦截器会自动注入到 ai_service 请求，作为授权过滤的
  // viewer_department_id 参数。
  const departmentId = computed(() => userScope.value?.departmentId ?? userScope.value?.department_id ?? null)
  const tenantId = computed(() => userScope.value?.tenantId ?? userScope.value?.tenant_id ?? null)
  const isSuperuser = computed(() => userScope.value.isSuperuser)
  const isEnterpriseAdmin = computed(() => userScope.value.isEnterpriseAdmin)
  const isEnterpriseSubUser = computed(() => userScope.value.isEnterpriseSubUser)
  const isPersonalIndependent = computed(() => userScope.value.isPersonalIndependent)
  const isBusinessTenant = computed(() => userScope.value.isBusinessTenant)
  const isPersonalTenant = computed(() => userScope.value.isPersonalTenant)
  const isEnterpriseUser = computed(() => userScope.value.isEnterpriseUser)
  const canAccessSystemSettings = computed(() => userScope.value.canAccessSystemSettings)

  /**
   * 用 payOrder 响应里的 quota 字段刷新 store 缓存。
   * - remaining_total_quota：扣款后支付平台返回的剩余总额度
   * - used_total_quota：扣款后累计已使用总额度
   * - total_quota：总额度上限
   * - call_id：模拟支付平台调用流水号
   */
  function setQuotaSnapshot({ remaining_total_quota, used_total_quota, total_quota, call_id } = {}) {
    if (remaining_total_quota !== undefined && remaining_total_quota !== null) {
      remainingTotalQuota.value = Number(remaining_total_quota) || 0
    }
    if (used_total_quota !== undefined && used_total_quota !== null) {
      usedTotalQuota.value = Number(used_total_quota) || 0
    }
    if (total_quota !== undefined && total_quota !== null) {
      totalQuota.value = Number(total_quota) || 0
    }
    if (call_id) {
      lastPaymentCallId.value = call_id
    }
  }
  
  async function login(credentials) {
    const formData = new URLSearchParams()
    formData.append('username', credentials.email)
    formData.append('password', credentials.password)
    const response = await authApi.login(formData)
    token.value = response.access_token
    tokenExpiresAt.value = Date.now() + LOGIN_SESSION_DURATION_MS
    sessionClock.value = Date.now()
    localStorage.setItem('token', response.access_token)
    localStorage.setItem(TOKEN_EXPIRES_AT_KEY, String(tokenExpiresAt.value))
    startSessionClock()
    await fetchUserInfo()
  }
  
  async function register(userData) {
    return await authApi.register(userData)
  }
  
  async function fetchUserInfo() {
    const storedToken = localStorage.getItem('token') || ''
    if (!storedToken || isJwtExpired(storedToken)) {
      token.value = ''
      userInfo.value = null
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      return null
    }

    token.value = storedToken
    try {
      userInfo.value = normalizeUserInfo(await userApi.getProfile())
      localStorage.setItem('userInfo', JSON.stringify(userInfo.value))
      return userInfo.value
    } catch (e) {
      console.error(e)
      return null
    }
  }
  
  function logout() {
    token.value = ''
    tokenExpiresAt.value = 0
    userInfo.value = null
    stopSessionClock()
    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
    localStorage.removeItem(TOKEN_EXPIRES_AT_KEY)
  }
  
  return {
    userInfo,
    token,
    remainingTotalQuota,
    usedTotalQuota,
    totalQuota,
    lastPaymentCallId,
    isLoggedIn,
    userName,
    userEmail,
    tenantType,
    planType,
    departmentId,
    tenantId,
    isSuperuser,
    isEnterpriseAdmin,
    isEnterpriseSubUser,
    isPersonalIndependent,
    isBusinessTenant,
    isPersonalTenant,
    isEnterpriseUser,
    canAccessSystemSettings,
    setQuotaSnapshot,
    login,
    register,
    logout,
    fetchUserInfo
  }
})
